<template>
  <v-data-table
    :headers="headers"
    :items="documents"
    hide-actions
    class="my-custom-table box-shadow no-background document-search-table">
    <template slot="items" slot-scope="props">
      <td class="document-search-file">
        <div class="document-search-file-inner">
          <font-awesome-icon
            class="document-search-file-icon mr-2"
            :style="{ color: fileIcon(props.item).color }"
            :icon="[fileIcon(props.item).iconSet, fileIcon(props.item).iconName]">
          </font-awesome-icon>
          <a
            href=""
            class="document-search-file-name document-search-link secondary--text"
            :title="props.item.filename"
            @click.prevent="openFile(props.item)">{{ props.item.filename }}</a>
        </div>
      </td>
      <td class="document-search-case">
        <router-link
          v-if="props.item.caseId"
          class="document-search-case-name document-search-link secondary--text"
          :title="props.item.caseTitle || ('Case ' + props.item.caseId)"
          :to="caseRoute(props.item)">
          {{ props.item.caseTitle || ("Case " + props.item.caseId) }}
        </router-link>
        <span v-else>—</span>
      </td>
      <td class="document-search-highlight" v-html="highlightHtml(props.item.highlight)"></td>
      <td class="document-search-size">{{ formatSize(props.item.size) }}</td>
      <td class="document-search-date">{{ formatDate(props.item.createdAt) }}</td>
      <td class="document-search-actions">
        <a
          href=""
          class="document-search-link secondary--text"
          @click.prevent="openFile(props.item)">Open document</a>
        <span v-if="props.item.caseId" class="document-search-actions-sep">·</span>
        <router-link
          v-if="props.item.caseId"
          class="document-search-link secondary--text"
          :to="caseRoute(props.item)">Open case</router-link>
      </td>
    </template>
  </v-data-table>
</template>

<script>
import Helpers from "@/helpers.js"
import { getIconForMimeType } from "@/lib/mimeTypeIcons.js"
import { mapState } from "vuex"

export default {
  name: "DocumentSearchResults",
  props: {
    documents: {
      type: Array,
      default: function() { return [] }
    }
  },
  data() {
    return {
      headers: [
        { text: "File", value: "filename", sortable: false, width: "28%" },
        { text: "Case", value: "caseTitle", sortable: false, width: "18%" },
        { text: "Match", value: "highlight", sortable: false, width: "28%" },
        { text: "Size", value: "size", sortable: false, width: "8%" },
        { text: "Date", value: "createdAt", sortable: false, width: "12%" },
        { text: "Open", value: "open", sortable: false, width: "6%" }
      ]
    }
  },
  computed: {
    ...mapState({
      userPreferences: state =>
        state.currentUser && state.currentUser.me && state.currentUser.me.preferences
          ? state.currentUser.me.preferences
          : {},
      searchTerm: state =>
        state.cbroute && state.cbroute.params && state.cbroute.params.search_term
          ? state.cbroute.params.search_term
          : ""
    })
  },
  methods: {
    fileIcon(item) {
      return getIconForMimeType(item.contentType, item.filename)
    },
    caseRoute(item) {
      return { name: "Case", params: { case_id: item.caseId } }
    },
    fileFetchUrl(path) {
      const documentPath = path || ""
      if (window.location.href.indexOf("http://localhost") === 0) {
        return "http://localhost:6543" + documentPath
      }
      return documentPath
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
    },
    wrapMatch(text) {
      return "<strong class=\"document-search-match\">" + text + "</strong>"
    },
    highlightFromEm(raw) {
      var text = String(raw)
      var out = ""
      var re = /<em>([\s\S]*?)<\/em>/gi
      var last = 0
      var match = re.exec(text)
      while (match) {
        out += this.escapeHtml(text.substring(last, match.index))
        out += this.wrapMatch(this.escapeHtml(match[1]))
        last = re.lastIndex
        match = re.exec(text)
      }
      out += this.escapeHtml(text.substring(last))
      return out.replace(/\s+/g, " ").trim()
    },
    highlightFromSearchTerm(plain) {
      var escaped = this.escapeHtml(plain).replace(/\s+/g, " ").trim()
      var term = String(this.searchTerm || "").trim()
      if (!escaped || !term) {
        return escaped
      }
      var tokens = term.split(/\s+/)
      var i
      for (i = 0; i < tokens.length; i++) {
        var token = tokens[i]
        if (!token || token.length < 2) {
          continue
        }
        var upper = token.toUpperCase()
        if (upper === "AND" || upper === "OR" || upper === "NOT") {
          continue
        }
        var safe = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        escaped = escaped.replace(new RegExp("(" + safe + ")", "gi"), this.wrapMatch("$1"))
      }
      return escaped
    },
    highlightHtml(highlight) {
      if (!highlight) {
        return ""
      }
      var raw = String(highlight)
      if (/<em[\s>]/i.test(raw)) {
        return this.highlightFromEm(raw)
      }
      return this.highlightFromSearchTerm(raw.replace(/<[^>]+>/g, " "))
    },
    highlightText(highlight) {
      if (!highlight) {
        return ""
      }
      return String(highlight).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
    },
    formatSize(bytes) {
      if (bytes === null || bytes === undefined || bytes === "") {
        return "—"
      }
      const value = Number(bytes)
      if (isNaN(value) || value < 0) {
        return "—"
      }
      if (value < 1024) {
        return value + " B"
      }
      if (value < 1024 * 1024) {
        return (value / 1024).toFixed(1) + " KB"
      }
      return (value / (1024 * 1024)).toFixed(1) + " MB"
    },
    formatDate(date) {
      if (!date) {
        return "—"
      }
      if (this.userPreferences && this.userPreferences.dateTimeFormat) {
        return Helpers.formatDate(date, this.userPreferences.dateTimeFormat)
      }
      return Helpers.formatDate(date, "Do MMM YYYY HH:mm")
    },
    async openFile(item) {
      try {
        const path = item && item.url ? item.url : ""
        const url = this.fileFetchUrl(path)
        const auth = "Bearer " + window.localStorage.session
        const anchor = document.createElement("a")
        document.body.appendChild(anchor)
        const headers = new Headers()
        headers.append("Authorization", auth)
        const response = await fetch(url, { headers: headers })
        if (!response.ok) {
          throw new Error("document fetch failed")
        }
        const blobby = await response.blob()
        const objectUrl = window.URL.createObjectURL(blobby)
        anchor.href = objectUrl
        anchor.target = "blank"
        anchor.click()
      } catch (err) {
        this.$store.commit("displayFlashMessage", { message: "Failed to view/download file", severity: "error" })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.document-search-table >>> .v-table__overflow {
  overflow-x: auto;
}
.document-search-table >>> table.v-table {
  table-layout: fixed;
  width: 100%;
  min-width: 0;
}
.document-search-table >>> table.v-table thead tr th,
.document-search-table >>> table.v-table tbody tr td {
  padding: 12px 16px !important;
  vertical-align: top;
  min-width: 0 !important;
  white-space: normal;
}
.document-search-table >>> table.v-table thead tr th:first-child,
.document-search-table >>> table.v-table tbody tr td:first-child {
  padding-left: 20px !important;
}
.document-search-table >>> table.v-table thead tr th:last-child,
.document-search-table >>> table.v-table tbody tr td:last-child {
  padding-right: 20px !important;
}
.document-search-file-inner {
  display: flex;
  align-items: flex-start;
  min-width: 0;
}
.document-search-file-icon {
  flex: 0 0 auto;
  margin-top: 2px;
}
.document-search-file-name,
.document-search-case-name,
.document-search-highlight {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: break-word;
}
.document-search-file-name {
  min-width: 0;
  flex: 1 1 auto;
}
.document-search-link {
  text-decoration: none;
}
.document-search-highlight {
  color: rgba(0, 0, 0, 0.54);
}
.document-search-highlight >>> .document-search-match {
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
}
.document-search-size,
.document-search-date {
  white-space: nowrap;
}
.document-search-actions {
  white-space: normal;
}
.document-search-actions .document-search-link {
  display: inline-block;
  margin: 0 8px 4px 0;
}
.document-search-actions-sep {
  margin: 0 8px 0 0;
  color: rgba(0, 0, 0, 0.38);
}
</style>
